import React, { useState, useEffect, useCallback, useMemo } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import * as yup from "yup";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//Services
import CorporateApi from "Services/Corporate";
import PassengerApi from "Services/Passenger";
import PassengerDocuments from "Pages/Modals/Passenger/Update/Passenger_documents";
import CorporateStatus from "Pages/Modals/Corporate/Update/Status";
import {
  buildEmptyPassengerDocuments,
  normalizePassengerDocuments,
} from "Pages/Modals/Passenger/Update/Passenger_documents/documents";

//Data
import countryCodeData from "./data/countryCode/index.json";
import { apiUrl } from "../../../../Constants";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`corporate-tabpanel-${index}`}
      aria-labelledby={`corporate-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `corporate-tab-${index}`,
    "aria-controls": `corporate-tabpanel-${index}`,
  };
}

const defaultAvatar = "/assets/images/user/avatar.png";
const CORPORATE_DOCUMENT_DEFINITIONS = [
  {
    id: 1,
    documentType: "Carte Nationale d'identité",
    documentTypeAlias: "NATIONAL_ID",
  },
  {
    id: 2,
    documentType: "Passeport National",
    documentTypeAlias: "NATIONAL_PASSPORT",
  },
];

const resolveCorporateImageUrl = (value) => {
  if (!value) {
    return defaultAvatar;
  }

  const stringValue = value.toString();
  if (
    stringValue.startsWith("http://") ||
    stringValue.startsWith("https://") ||
    stringValue.startsWith("data:") ||
    stringValue.startsWith("blob:")
  ) {
    return stringValue;
  }

  return `${apiUrl}${stringValue}`;
};

const CORPORATE_ALLOWED_ACCOUNT_STATUSES = ["ONBOARDING", "APPROVED"];

const resolveCorporateAccountStatus = (details = {}) => {
  const rawStatus =
    details?.customer_details?.accountStatus ?? details?.accountStatus ?? details?.status ?? "";
  const normalizedStatus = rawStatus.toString().trim().toUpperCase();

  if (normalizedStatus === "APPROVED" || normalizedStatus === "ONBOARDING") {
    return normalizedStatus;
  }

  return Number(details?.isActive) === 1 ? "APPROVED" : "ONBOARDING";
};

const resolveCorporatePassengerStatus = (details = {}) => {
  const rawStatus =
    details?.passengerAccountStatus ??
    details?.customer_details?.passengerAccountStatus ??
    "";
  const normalizedStatus = rawStatus.toString().trim().toUpperCase();

  if (normalizedStatus === "ACTIVE" || normalizedStatus === "DE_ACTIVE") {
    return normalizedStatus;
  }

  return Number(details?.isActive) === 1 ? "ACTIVE" : "DE_ACTIVE";
};

const SubcmpnyUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [details, setDetails] = useState({});
  const [corporateDocumentTypes, setCorporateDocumentTypes] = useState(CORPORATE_DOCUMENT_DEFINITIONS);
  const [corporateDocuments, setCorporateDocuments] = useState(
    buildEmptyPassengerDocuments(CORPORATE_DOCUMENT_DEFINITIONS)
  );
  const [corporateDocumentRecords, setCorporateDocumentRecords] = useState([]);
  const [value, setValue] = useState(0);
  const [skipButton, setSkipButton] = useState(false);
  const [backButton, setBackButton] = useState(false);
  const [profilePreview, setProfilePreview] = useState(defaultAvatar);

// Max file size
const maxFileSize = 5242880;
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};
// File length validator
const fileLengthValidator = (file) => {
  if (file.size > maxFileSize) {
    alert(`File size is larger than ${formatBytes(maxFileSize)}`);
    return {
      code: "file-too-large",
      message: `File size is larger than ${formatBytes(maxFileSize)}`,
    };
  }
  return null;
};

  // Dropzone props
  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0 && acceptedFiles[0].size <= maxFileSize) {
        const file = acceptedFiles[0];
      //console.log("typeof file-->", typeof(file));

        // Convert file to Base64
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("passengerProfileImagefile", file);
          formik.setFieldValue("passengerProfileImage", base64String);
          setProfilePreview(base64String);
        };
        reader.onerror = (error) => {
          console.error("Error converting file to Base64:", error);
        };
        reader.readAsDataURL(file); // Convert file to Base64
      }
    },
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (value === 0) {
      setSkipButton(false);
      setBackButton(false);
    }

    if (value === 1) {
      setSkipButton(true);
      setBackButton(true);
    }

    if (value === 2) {
      setSkipButton(false);
      setBackButton(true);
    }
  }, [value]);

  const handleBackButton = () => {
    setValue((prev) => Math.max(prev - 1, 0));
  };

  const handleSkipButton = () => {
    setValue((prev) => Math.min(prev + 1, 2));
  };

  const handleOpenCorporateDocumentsTab = () => {
    setValue(1);
  };

  const handleSubmitAndNext = () => {
    if (value === 0) {
      formik.handleSubmit();
      return;
    }

    if (value === 1) {
      setValue(2);
      return;
    }

    if (value === 2) {
      handleCloseAndReset();
    }
  };

  const handleCloseAndReset = () => {
    setValue(0);
    setSkipButton(false);
    setBackButton(false);
    setDetails({});
    setCorporateDocumentTypes(CORPORATE_DOCUMENT_DEFINITIONS);
    setCorporateDocumentRecords([]);
    setCorporateDocuments(
      buildEmptyPassengerDocuments(CORPORATE_DOCUMENT_DEFINITIONS)
    );
    setProfilePreview(defaultAvatar);
    formik.resetForm();
    handleClose();
  };

  // Fetch corporate details
  const getDetails = useCallback(() => {
    if (!id) {
      return;
    }

    dispatch(showLoader("Loading please wait..."));

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.status === 200) {
        setDetails(res?.data?.data || {});
      } else {
        setDetails({});
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    CorporateApi.getCorporateDetails(id).then(onSuccess, onFailure);
  }, [dispatch, id]);

  const getCorporateDocumentTypes = useCallback(() => {
    const onSuccess = (res) => {
      if (res?.status === 200) {
        const payload = res?.data?.data;
        const candidates = [payload, payload?.data, payload?.documents, payload?.documentTypes, payload?.document_types, payload?.items, payload?.list];
        for (const candidate of candidates) {
          if (Array.isArray(candidate) && candidate.length > 0) {
            setCorporateDocumentTypes(candidate);
            return;
          }
        }
      }
      setCorporateDocumentTypes(CORPORATE_DOCUMENT_DEFINITIONS);
    };

    const onFailure = () => {
      setCorporateDocumentTypes(CORPORATE_DOCUMENT_DEFINITIONS);
    };

    PassengerApi.getDocumentsTypes("CORPORATE").then(onSuccess, onFailure);
  }, []);

  const getCorporateDocuments = useCallback(() => {
    if (!id) {
      return;
    }

    const onSuccess = (res) => {
      const rawData = res?.data?.data;
      const documentRecords = Array.isArray(rawData?.driverDocuments)
        ? rawData.driverDocuments
        : Array.isArray(rawData?.documents)
        ? rawData.documents
        : Array.isArray(rawData?.data)
        ? rawData.data
        : Array.isArray(rawData)
        ? rawData
        : [];

      setCorporateDocumentRecords(documentRecords);
    };

    const onFailure = (err) => {
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch corporate documents",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setCorporateDocumentRecords([]);
    };

    PassengerApi.getPassengerDocumentsByPassengerId(id, { userType: "CORPORATE" }).then(onSuccess, onFailure);
  }, [dispatch, id]);

  useEffect(() => {
    if (openDialog && id) {
      setValue(0);
      setCorporateDocumentTypes(CORPORATE_DOCUMENT_DEFINITIONS);
      setCorporateDocumentRecords([]);
      getDetails();
      getCorporateDocumentTypes();
      getCorporateDocuments();
    }
  }, [openDialog, id, getDetails, getCorporateDocumentTypes, getCorporateDocuments]);

  const handleUpdateSubcmpny = (formValues) => {
    if (!id) {
      return;
    }

    dispatch(showLoader("Loading please wait..."));

    const data = {
      firstName: formValues?.firstName,
      displayName: formValues?.displayName,
      email: formValues?.email,
      secondaryEmail: formValues?.secondaryEmail,
      panNumber: formValues?.panNumber,
      country: formValues?.country,
      mobileNumber: formValues?.mobile,
      address: formValues?.address,
      smsMailenable: formValues?.smsMailenable,
      isActive: formValues?.isActive,
    };

    if (formValues?.passengerProfileImagefile instanceof Blob) {
      data.image = formValues?.passengerProfileImagefile;
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.status === 200) {
        dispatch(
          showSnackbar({
            message: "Sub-company Details Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );

        if (value === 0) {
          setValue(1);
        } else {
          handleCloseAndReset();
        }
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while Update Sub-company",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    CorporateApi.updateCorporate(id, { ...data }).then(onSuccess, onFailure);
  };

  // Formik
  const formik = useFormik({
    initialValues: {
      firstName: "",
      displayName: "",
      email: "",
      secondaryEmail: "",
      panNumber: "",
      country: "",
      mobile: "",
      address: "",
      smsMailenable: false,
      passengerProfileImage: "",
      passengerProfileImagefile: "",
      isActive: 1,
    },
    validationSchema: yup.object({
      firstName: yup.string().required("First Name is required").nullable(),
      displayName: yup.string().required("display Name is required").nullable(),
      email: yup
        .string()
        .email("Invalid email format")
        .required("Email is required")
        .nullable(),
      secondaryEmail: yup
        .string()
        .email("Invalid email format")
        .required("Secondary Email is required")
        .nullable(),
      mobile: yup
        .string()
        .matches(/^\d{1,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      panNumber: yup.string().required("Pan Number is required").nullable(),
      country: yup.string().required("Country is required").nullable(),
      address: yup.string().required("address is required").nullable(),
      smsMailenable: yup
        .boolean()
        .required("Choose enable or disable is required")
        .nullable(),
      passengerProfileImage: yup
        .string()
        .required("Corporate Logo is required")
        .nullable(),
      isActive: yup.number().required("Status is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdateSubcmpny,
  });
  const { setValues } = formik;
  const submitButtonLabel = value === 2 ? "Save & Close" : "Submit & Next";

  useEffect(() => {
    if (!openDialog || _.isEmpty(details)) {
      return;
    }

    const corporateImage = resolveCorporateImageUrl(
      details?.image_local || details?.image || ""
    );

    setProfilePreview(corporateImage);
    setValues({
      firstName: details?.firstName || "",
      displayName: details?.corporateDetails?.displayName || "",
      email: details?.email || "",
      secondaryEmail: details?.corporateDetails?.secondaryEmail || "",
      panNumber: details?.corporateDetails?.panNumber || "",
      country: details?.country || "",
      mobile: details?.mobileNumber || "",
      address: details?.address || "",
      smsMailenable: Boolean(details?.corporateDetails?.smsMailenable),
      passengerProfileImage: corporateImage,
      passengerProfileImagefile: corporateImage,
      isActive: Number(details?.isActive) === 1 ? 1 : 0,
    });
  }, [details, openDialog, setValues]);

  useEffect(() => {
    if (!openDialog) {
      return;
    }

    setCorporateDocuments(
      normalizePassengerDocuments(
        details,
        corporateDocumentTypes,
        corporateDocumentRecords
      )
    );
  }, [details, openDialog, corporateDocumentTypes, corporateDocumentRecords]);

  const corporateStatusDetails = useMemo(
    () => ({
      ...details,
      corporateDetails: {
        ...(details?.corporateDetails || {}),
        accountStatus: resolveCorporateAccountStatus(details),
      },
      passengerAccountStatus: resolveCorporatePassengerStatus(details),
    }),
    [details]
  );

  return (
    <>
      <Dialog
        open={openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseAndReset}
        maxWidth={"xl"}
        aria-describedby="dialog-box"
        className={classes.root}
      >
        <DialogTitle>
          <section className={classes.dialogTitleContainer}>
            <Typography variant="body1" className={classes.title}>
              Update Corporate
            </Typography>
            <IconButton aria-label="close" onClick={handleCloseAndReset}>
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </section>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={value} onChange={handleChange} aria-label="corporate update tabs">
                <Tab label="Basic Information" {...a11yProps(0)} />
                <Tab label="Documents" {...a11yProps(1)} />
                <Tab label="Status" {...a11yProps(2)} />
              </Tabs>
            </Box>

            <CustomTabPanel value={value} index={0}>
              <Grid container>
                <Grid item xs={12}>
                  <section className={classes.avatarContainer}>
                    <div className={classes.avatarWrapper}>
                      <div className={classes.avatar}>
                        <img
                          src={formik?.values?.passengerProfileImage || profilePreview}
                          alt="avatar"
                          className={classes.img}
                          style={{ objectFit: "cover" }}
                        />
                        <section
                          {...getRootProps()}
                          onClick={open}
                          className={classes.editIcon}
                        >
                          <input {...getInputProps()} />
                          <CameraAltOutlinedIcon />
                        </section>
                      </div>
                    </div>
                  </section>
                </Grid>
              </Grid>

              <Grid container>
                <section className={classes.container}>
                  <Grid item xs={12} className={classes.detailsContainer}>
                    <Grid container className={classes.containerWrapper}>
                      <Grid item xs={12} md={6} lg={6} xl={6}>
                        <Grid container>
                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              id="firstName"
                              name="firstName"
                              label="Legal Name"
                              value={formik?.values?.firstName}
                              onChange={formik.handleChange}
                              error={
                                formik?.errors?.firstName &&
                                formik?.touched?.firstName
                              }
                              helperText={
                                formik?.errors?.firstName &&
                                formik?.touched?.firstName &&
                                formik?.errors?.firstName
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="displayName"
                              id="displayName"
                              label="Display Name"
                              value={formik?.values?.displayName}
                              onChange={formik?.handleChange}
                              error={
                                formik?.errors?.displayName &&
                                formik?.touched?.displayName
                              }
                              helperText={
                                formik?.errors?.displayName &&
                                formik?.touched?.displayName &&
                                formik?.errors?.displayName
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                          <Grid item xs={12} md={12} lg={12} xl={12}>
                            <Grid container>
                              <Grid
                                item
                                xs={12}
                                md={12}
                                lg={12}
                                xl={12}
                                className={classes.textFieldWrapper}
                              >
                                <TextField
                                  inputProps={{
                                    form: {
                                      autoComplete: "off",
                                    },
                                  }}
                                  name="mobile"
                                  id="mobile"
                                  label="Mobile Number"
                                  value={formik?.values?.mobile}
                                  onChange={formik?.handleChange}
                                  error={
                                    formik?.errors?.mobile &&
                                    formik?.touched?.mobile
                                  }
                                  helperText={
                                    formik?.errors?.mobile &&
                                    formik?.touched?.mobile &&
                                    formik?.errors?.mobile
                                  }
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="email"
                              id="email"
                              label="Email"
                              value={formik?.values?.email}
                              onChange={formik?.handleChange}
                              error={formik?.errors?.email && formik?.touched?.email}
                              helperText={
                                formik?.errors?.email &&
                                formik?.touched?.email &&
                                formik?.errors?.email
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="secondaryEmail"
                              id="secondaryEmail"
                              label="Secondary Email"
                              value={formik?.values?.secondaryEmail}
                              onChange={formik?.handleChange}
                              error={
                                formik?.errors?.secondaryEmail &&
                                formik?.touched?.secondaryEmail
                              }
                              helperText={
                                formik?.errors?.secondaryEmail &&
                                formik?.touched?.secondaryEmail &&
                                formik?.errors?.secondaryEmail
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            xl={12}
                            className={classes.textFieldWrapper}
                          >
                            <TextField
                              inputProps={{
                                form: {
                                  autoComplete: "off",
                                },
                              }}
                              name="panNumber"
                              id="panNumber"
                              label="Pan Number"
                              value={formik?.values?.panNumber}
                              onChange={formik?.handleChange}
                              error={
                                formik?.errors?.panNumber &&
                                formik?.touched?.panNumber
                              }
                              helperText={
                                formik?.errors?.panNumber &&
                                formik?.touched?.panNumber &&
                                formik?.errors?.panNumber
                              }
                              fullWidth
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} md={6} lg={6} xl={6}>
                        <Grid
                          item
                          xs={12}
                          md={12}
                          lg={12}
                          xl={12}
                          className={classes.textFieldWrapper}
                        >
                          <TextField
                            inputProps={{
                              form: {
                                autoComplete: "off",
                              },
                            }}
                            name="address"
                            id="address"
                            label="Address"
                            value={formik?.values?.address}
                            onChange={formik?.handleChange}
                            error={formik?.errors?.address && formik?.touched?.address}
                            helperText={
                              formik?.errors?.address &&
                              formik?.touched?.address &&
                              formik?.errors?.address
                            }
                            fullWidth
                            variant="outlined"
                            size="small"
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={12}
                          lg={12}
                          xl={12}
                          className={classes.textFieldWrapper}
                        >
                          <TextField
                            inputProps={{
                              form: {
                                autoComplete: "off",
                              },
                            }}
                            name="country"
                            id="country"
                            select
                            label="Country"
                            value={formik?.values?.country || ""}
                            onChange={formik?.handleChange}
                            error={formik?.errors?.country && formik?.touched?.country}
                            helperText={
                              formik?.errors?.country &&
                              formik?.touched?.country &&
                              formik?.errors?.country
                            }
                            fullWidth
                            variant="outlined"
                            size="small"
                          >
                            {countryCodeData.map((country) => (
                              <MenuItem key={country.code} value={country.name}>
                                {`${country.code} (${country.name})`}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={12}
                          lg={12}
                          xl={12}
                          className={classes.textFieldWrapper}
                        >
                          <TextField
                            inputProps={{
                              form: {
                                autoComplete: "off",
                              },
                            }}
                            name="smsMailenable"
                            id="smsMailenable"
                            select
                            label="SMS/Mail for Customers"
                            value={formik?.values?.smsMailenable}
                            onChange={(event) =>
                              formik.setFieldValue(
                                "smsMailenable",
                                event.target.value === "true" || event.target.value === true
                              )
                            }
                            error={
                              Boolean(formik?.errors?.smsMailenable) &&
                              formik?.touched?.smsMailenable
                            }
                            helperText={
                              formik?.errors?.smsMailenable &&
                              formik?.touched?.smsMailenable &&
                              formik?.errors?.smsMailenable
                            }
                            fullWidth
                            variant="outlined"
                            size="small"
                          >
                            <MenuItem key="enabled" value={true}>
                              Enabled
                            </MenuItem>
                            <MenuItem key="disabled" value={false}>
                              Disabled
                            </MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </section>
              </Grid>
            </CustomTabPanel>

            <CustomTabPanel value={value} index={1}>
              <PassengerDocuments
                passengerId={id}
                documents={corporateDocuments}
                setDocuments={setCorporateDocuments}
              />
            </CustomTabPanel>

            <CustomTabPanel value={value} index={2}>
              <CorporateStatus
                passengerId={id}
                details={corporateStatusDetails}
                passengerDocuments={corporateDocuments}
                openDocumentsTab={handleOpenCorporateDocumentsTab}
                userType="CORPORATE"
                allowedAccountStatuses={CORPORATE_ALLOWED_ACCOUNT_STATUSES}
              />
            </CustomTabPanel>

          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <section className={classes.dialogActions}>
            {backButton && (
              <section className={classes.skipBtn}>
                <Button onClick={handleBackButton}>Back</Button>
              </section>
            )}

            {skipButton && (
              <section className={classes.skipBtn}>
                <Button onClick={handleSkipButton}>Skip</Button>
              </section>
            )}

            <section className={classes.submBtn}>
              <Button onClick={handleSubmitAndNext}>{submitButtonLabel}</Button>
            </section>
          </section>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubcmpnyUpdate;
